/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetOrganizationGatewayRequest,
  GetOrganizationGatewaySecurity,
} from "../models/operations/getorganizationgateway.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationGatewayQuery,
  OrganizationGatewayQueryData,
  prefetchOrganizationGateway,
  queryKeyOrganizationGateway,
} from "./organizationGateway.core.js";
export {
  buildOrganizationGatewayQuery,
  type OrganizationGatewayQueryData,
  prefetchOrganizationGateway,
  queryKeyOrganizationGateway,
};

export type OrganizationGatewayQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getGateway organizations
 *
 * @remarks
 * Get the active organization's gateway: one MCP URL that gives each member every MCP server in the organization they can reach, signed in through the organization's own user session issuer. It is served while the organization owns exactly one organization-level user session issuer, on the platform host, so not for an organization whose custom domain restricts access to an IP allowlist, not while an organization admin has turned it off, and not for a disabled organization. Requires org:read.
 */
export function useOrganizationGateway(
  request?: GetOrganizationGatewayRequest | undefined,
  security?: GetOrganizationGatewaySecurity | undefined,
  options?: QueryHookOptions<
    OrganizationGatewayQueryData,
    OrganizationGatewayQueryError
  >,
): UseQueryResult<OrganizationGatewayQueryData, OrganizationGatewayQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationGatewayQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getGateway organizations
 *
 * @remarks
 * Get the active organization's gateway: one MCP URL that gives each member every MCP server in the organization they can reach, signed in through the organization's own user session issuer. It is served while the organization owns exactly one organization-level user session issuer, on the platform host, so not for an organization whose custom domain restricts access to an IP allowlist, not while an organization admin has turned it off, and not for a disabled organization. Requires org:read.
 */
export function useOrganizationGatewaySuspense(
  request?: GetOrganizationGatewayRequest | undefined,
  security?: GetOrganizationGatewaySecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationGatewayQueryData,
    OrganizationGatewayQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationGatewayQueryData,
  OrganizationGatewayQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationGatewayQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setOrganizationGatewayData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: OrganizationGatewayQueryData,
): OrganizationGatewayQueryData | undefined {
  const key = queryKeyOrganizationGateway(...queryKeyBase);

  return client.setQueryData<OrganizationGatewayQueryData>(key, data);
}

export function invalidateOrganizationGateway(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "organizations", "getGateway", ...queryKeyBase],
  });
}

export function invalidateAllOrganizationGateway(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "organizations", "getGateway"],
  });
}
